import React, { useState } from 'react'

import { DownloadIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { CandidatePictureAlternative } from '@pkg/types/interview'

import {
  useCandidate,
  useUpdateCandidate,
  useUploadCandidatePicture,
} from '@/react-query/candidate'
import { LinkedinAvatarFallback } from '@/views/assets/LinkedinAvatarFallback'
import FileUploader from '@/views/components/FileUploader'

export const SelectPictureModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { data: candidate } = useCandidate()
  if (!candidate) {
    return null
  }

  const updateCandidate = useUpdateCandidate()

  let pictureAlternatives = candidate.PictureAlternatives || []

  // This handles old interview, when we didn't save picture to alternatives
  if (
    candidate.profilePictureURL &&
    !pictureAlternatives.find((p) => p.url === candidate.profilePictureURL)
  ) {
    pictureAlternatives = [
      {
        url: candidate.profilePictureURL,
        source: 'upload',
      },
      ...pictureAlternatives,
    ]
  }

  const handleSelectPicture = (url: string) => {
    const updatedCandidate = {
      profilePictureURL: url,
      id: candidate.id,
    }

    updateCandidate.mutate({ candidate: updatedCandidate })
  }

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent p={5}>
        <ModalHeader>Edit candidate&apos;s picture</ModalHeader>
        <ModalCloseButton />
        <ModalBody display={'flex'} flexDirection={'column'}>
          {candidate.profilePictureURL ? (
            <Avatar
              src={candidate.profilePictureURL}
              w={40}
              h={40}
              alignSelf={'center'}
              bg={'transparent'}
              icon={<Box bg={'transparent'} />}
              borderRadius={'2.5rem'}
            />
          ) : (
            <LinkedinAvatarFallback
              h={40}
              w={40}
              alignSelf={'center'}
              borderRadius={'2.5rem'}
            />
          )}

          <HStack
            justifyContent={'center'}
            alignItems={'center'}
            mt={10}
            flexWrap={'wrap-reverse'}
          >
            {pictureAlternatives?.map((picture, index) => (
              <AlternativePicture
                key={index}
                picture={picture}
                handleSelectPicture={handleSelectPicture}
              />
            ))}
            <UploadNewPicture />
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const UploadNewPicture = () => {
  const uploadCandidatePicture = useUploadCandidatePicture()

  const [isLoading, setIsLoading] = useState(false)
  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    try {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }
      setIsLoading(true)

      const formData = new FormData()
      formData.append('files', file)

      await uploadCandidatePicture.mutateAsync(formData)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FileUploader
      name="candidatePicture"
      id="candidatePicture"
      acceptedFileTypes=".jpg,.jpeg,.png,.webp,.gif"
      w={20}
      h={20}
      onFileChange={handleFileChange}
      isLoading={isLoading}
    >
      <DownloadIcon
        color={'blackAlpha.700'}
        w={5}
        h={5}
        transform="rotate(180deg)"
      />
    </FileUploader>
  )
}

const AlternativePicture = ({
  picture,
  handleSelectPicture,
}: {
  picture: CandidatePictureAlternative
  handleSelectPicture: (url: string) => void
}) => {
  const candidate = useCandidate().data
  if (!candidate) {
    return null
  }

  const isSelected = candidate.profilePictureURL === picture.url

  const onSelectPicture = async () => {
    try {
      handleSelectPicture(picture.url)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <Button
      position="relative"
      display="inline-block"
      w={'auto'}
      h={'auto'}
      p={0}
      borderRadius={'1.25rem'}
      cursor={isSelected ? 'default' : 'pointer'}
      onClick={onSelectPicture}
    >
      <Avatar
        icon={<LinkedinAvatarFallback w={20} h={20} borderRadius={'1.25rem'} />}
        src={picture.url}
        w={20}
        h={20}
        borderRadius={'1.25rem'}
      />
      {
        // overlay for selected picture
        isSelected && (
          <Box
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            right={0}
            w={'full'}
            h={'full'}
            bg="blackAlpha.300"
            alignItems="center"
            justifyContent="center"
            display="flex"
            borderRadius={'1.25rem'}
          >
            <Box w={50} h={50} color="whiteAlpha.800">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 21"
                fill="none"
              >
                <path
                  d="M4 9.84L7.18105 14.2935C7.75661 15.0993 8.96951 15.0508 9.47898 14.2017L15 5"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </Box>
          </Box>
        )
      }
    </Button>
  )
}
